import { useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2 } from "lucide-react";

export default function ImportExport() {
  const { user } = useAuth();
  // L'export sort la base entière : réservé à l'admin, comme sur la liste des
  // entreprises (le serveur renvoie 403 aux autres rôles).
  const isAdmin = user?.role === "admin";
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [format, setFormat] = useState("csv");

  const onFile = async (f) => {
    setFile(f); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirm = async () => {
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/import/confirm", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} ajoutées, ${data.updated} mises à jour`);
      setFile(null); setPreview(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setImporting(false);
  };

  const exportAll = async () => {
    try {
      const { data } = await api.get("/export/companies", { responseType: "blob", params: { format } });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `prospects.${format}`; a.click(); URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Échec de l'export"); }
  };

  return (
    <div>
      <PageHeader title={isAdmin ? "Import / Export" : "Import"}
        subtitle={isAdmin ? "Importez vos fichiers CSV ou exportez votre base" : "Importez vos fichiers CSV"} />
      {/* Sans la carte Export, l'import occupe seul la largeur. */}
      <div className={`grid grid-cols-1 gap-4 ${isAdmin ? "lg:grid-cols-2" : ""}`}>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading font-semibold text-slate-800 mb-1 flex items-center gap-2"><Upload size={18} /> Import CSV</h3>
          <p className="text-xs text-slate-500 mb-4">Colonnes reconnues : siret, nom, telephone, email, site_web, adresse, code_postal, ville, departement, naf</p>
          <label className="border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400" data-testid="import-dropzone">
            <FileSpreadsheet size={36} className="text-slate-300 mb-2" />
            <span className="text-sm text-slate-500">{file ? file.name : "Cliquez pour choisir un fichier .csv"}</span>
            <input type="file" accept=".csv" className="hidden" data-testid="import-file" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
          </label>
          {preview && (
            <div className="mt-4" data-testid="import-preview">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xl font-bold font-mono text-slate-800">{preview.total}</div><div className="text-xs text-slate-500">lignes</div></div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center"><div className="text-xl font-bold font-mono text-emerald-700">{preview.new}</div><div className="text-xs text-slate-500">nouvelles</div></div>
                <div className="bg-amber-50 rounded-lg p-3 text-center"><div className="text-xl font-bold font-mono text-amber-700">{preview.existing}</div><div className="text-xs text-slate-500">existantes</div></div>
              </div>
              <button data-testid="confirm-import" onClick={confirm} disabled={importing} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-60">
                <CheckCircle2 size={16} /> {importing ? "Import…" : "Confirmer l'import"}
              </button>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-heading font-semibold text-slate-800 mb-1 flex items-center gap-2"><Download size={18} /> Export</h3>
            <p className="text-xs text-slate-500 mb-4">Exportez l'ensemble de vos entreprises (selon vos droits) au format CSV ou Excel. Colonnes incluses : secteur NAF, score IA, code NAF…</p>
            <div className="flex items-center gap-2">
              <select data-testid="export-format" value={format} onChange={(e) => setFormat(e.target.value)} className="px-3 py-2.5 rounded-lg border border-slate-300 text-sm bg-white">
                <option value="csv">CSV</option>
                <option value="xlsx">XLSX (Excel)</option>
              </select>
              <button data-testid="export-all-btn" onClick={exportAll} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
                <Download size={16} /> Exporter toutes les entreprises
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
